/* ============================================================
   BloomUp — cart drawer coupon box + offers popup

   BEHAVIOUR (owner decision, 20 Aug 2026 — final): discounts are ONLY
   discoverable in the View Coupons popup and ONLY applied when the customer
   taps Apply or types a code. No recommendation buttons, no auto-apply
   (assets/bloomup-tierdiscount.js, AUTO_APPLY = false).

   Data comes from the .bu-coupon-json block rendered by
   snippets/bloomup-coupons.liquid, which in turn reads the shop metafield
   bloomup.cart_coupons. Shopify gives the storefront no access to discounts,
   so that metafield is the only bridge.

   Eligibility and the "add X more" amount are computed in Liquid, not here,
   so the money is formatted for the active market and keeps the markup that
   currency converter apps rely on. That markup is injected as HTML; every
   other value is escaped.

   APPLYING / REMOVING — method order:
   1. PRIMARY: POST /cart/update.js with { discount: "CODE" } (or "" to
      clear). This is the modern Cart AJAX discounts API and returns the
      updated cart directly, so success is verified from the response.
   2. FALLBACK: the legacy /discount/CODE URL (sets the code on the session),
      then /cart.js to verify. Kept for older storefront behaviour. The old
      "remove by requesting an invalid code" trick is unreliable on current
      Shopify and is only used as a last resort.
   Either way we VERIFY the cart afterwards and say so plainly if the code
   did not land or did not clear — never fake success.

   MANUAL OVERRIDE: once the customer applies or removes a coupon by hand, a
   flag is set in sessionStorage and assets/bloomup-tierdiscount.js stops
   auto-applying tier codes for the rest of the session. (Auto-apply is
   currently disabled entirely, but the flag is kept so re-enabling it
   later cannot fight manual choices.)
   ============================================================ */
(function () {
  "use strict";

  var MANUAL_KEY = "bu-coupon-manual";
  var busy = false;

  function drawer() { return document.querySelector("#CartDrawer"); }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function markManual() {
    try { window.sessionStorage.setItem(MANUAL_KEY, "1"); } catch (e) {}
  }

  function data() {
    var el = document.querySelector("#CartDrawer .bu-coupon-json");
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.json(); });
  }

  /* the code currently on the cart, uppercased, or "" — checks both the
     discount applications and the newer discount_codes array */
  function appliedFrom(cart) {
    if (!cart) return "";
    var codes = cart.discount_codes;
    if (codes && codes.length) {
      for (var j = 0; j < codes.length; j++) {
        if (codes[j] && codes[j].code && codes[j].applicable !== false) {
          return String(codes[j].code).toUpperCase();
        }
      }
    }
    var apps = cart.cart_level_discount_applications || [];
    for (var i = 0; i < apps.length; i++) {
      if (apps[i] && apps[i].title) return String(apps[i].title).toUpperCase();
    }
    return "";
  }

  /* modern Cart AJAX: set (or clear, with "") the cart's discount code.
     Resolves with the updated cart JSON, or null if the endpoint refused. */
  function setCartDiscount(code) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ discount: code })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* legacy fallback: the /discount/CODE session URL */
  function legacyDiscountUrl(code) {
    return fetch("/discount/" + encodeURIComponent(code) + "?redirect=/cart.js", {
      credentials: "same-origin"
    }).then(getCart).catch(function () { return null; });
  }

  function message(text, ok) {
    var box = document.querySelector("#CartDrawer .bu-coupon");
    if (!box) return;
    var el = box.querySelector(".bu-coupon-msg");
    if (!text) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement("p");
      el.className = "bu-coupon-msg";
      var wrap = box.querySelector(".bu-coupon-input-wrap");
      if (wrap && wrap.parentNode) wrap.parentNode.insertBefore(el, wrap.nextSibling);
      else box.appendChild(el);
    }
    el.textContent = text;
    el.classList.toggle("is-ok", !!ok);
  }

  function refresh() {
    if (window.BUCart && window.BUCart.rerender) window.BUCart.rerender();
  }

  /* ---------------- applying ---------------- */
  function apply(code, onDone) {
    code = String(code || "").trim().toUpperCase();
    if (!code || busy) { if (onDone) onDone(false); return; }
    busy = true;
    markManual();
    message("Applying\u2026", true);

    function finish(worked) {
      busy = false;
      if (worked) {
        message("", true);
        closeSheet();
        refresh();
        if (onDone) onDone(true);
      } else {
        message("That code can't be used with this cart.", false);
        if (onDone) onDone(false);
      }
    }

    getCart()
      .then(function (before) {
        var wasTotal = before ? before.total_price : null;

        function landed(after) {
          if (!after) return false;
          if (appliedFrom(after) === code) return true;
          return wasTotal != null && after.total_price < wasTotal;
        }

        setCartDiscount(code).then(function (after) {
          if (landed(after)) { finish(true); return; }
          /* update.js refused or didn't take — try the legacy session URL */
          legacyDiscountUrl(code).then(function (after2) {
            finish(landed(after2));
          });
        });
      })
      .catch(function () {
        busy = false;
        message("Something went wrong. Please try again.", false);
        if (onDone) onDone(false);
      });
  }

  /* ---------------- removing ---------------- */
  function remove(onDone) {
    if (busy) { if (onDone) onDone(false); return; }
    busy = true;
    markManual();
    message("Removing\u2026", true);

    function finish(cleared) {
      busy = false;
      if (cleared) {
        message("", true);
        closeSheet();
        refresh();
        if (onDone) onDone(true);
      } else {
        message("Couldn't remove that coupon. You can change it at checkout.", false);
        if (onDone) onDone(false);
      }
    }

    /* primary: clear via Cart AJAX */
    setCartDiscount("").then(function (after) {
      if (after && !appliedFrom(after)) { finish(true); return; }
      /* fallback: legacy invalid-code trick, then verify honestly */
      var dummy = "BU-NONE-" + Date.now().toString(36).toUpperCase();
      legacyDiscountUrl(dummy).then(function (after2) {
        finish(!!after2 && !appliedFrom(after2));
      });
    });
  }

  /* ---------------- popup ---------------- */
  function sheetEl() {
    var d = drawer();
    if (!d) return null;
    var el = d.querySelector(".bu-cp-sheet");
    if (el) return el;
    el = document.createElement("div");
    el.className = "bu-cp-sheet";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-label", "Available offers");
    el.innerHTML =
      '<div class="bu-cp-panel">' +
      '<div class="bu-cp-handle" role="button" aria-label="Close"></div>' +
      '<div class="bu-cp-head">' +
      '<p class="bu-cp-title">Available Offers</p>' +
      '<button type="button" class="bu-cp-close" aria-label="Close">&times;</button>' +
      "</div>" +
      '<div class="bu-cp-body"></div>' +
      "</div>";
    d.appendChild(el);
    return el;
  }

  function cardFor(c, applied) {
    var eligible = c.eligible === true;
    var isApplied = applied && applied === c.code;

    var action;
    if (isApplied) {
      action = '<button type="button" class="bu-cp-action is-remove" data-remove="1">Remove</button>';
    } else if (eligible) {
      action = '<button type="button" class="bu-cp-action" data-code="' + esc(c.code) + '">Apply</button>';
    } else {
      action = '<button type="button" class="bu-cp-action" disabled>Apply</button>';
    }

    var notes = "";
    if (c.notes && c.notes.length) {
      notes = '<ul class="bu-cp-notes">' +
        c.notes.map(function (n) { return "<li>" + esc(n) + "</li>"; }).join("") +
        "</ul>";
    }

    /* remaining_money is Liquid-rendered money markup — injected as HTML */
    var locked = "";
    if (!eligible && c.remaining_money) {
      locked = '<p class="bu-cp-locked">Add ' + c.remaining_money + " more to use this offer</p>";
    }

    return (
      '<div class="bu-cp-card' + (isApplied ? " is-applied" : "") + '">' +
      '<div class="bu-cp-row">' +
      '<span class="bu-cp-badge" aria-hidden="true"></span>' +
      '<span class="bu-cp-code">' + esc(c.code) + "</span>" +
      action +
      "</div>" +
      (c.title ? '<p class="bu-cp-desc">' + esc(c.title) + "</p>" : "") +
      notes +
      locked +
      "</div>"
    );
  }

  function openSheet() {
    var d = data();
    var sheet = sheetEl();
    if (!sheet) return;
    var body = sheet.querySelector(".bu-cp-body");
    var list = (d && d.coupons) || [];

    /* de-dupe by code — belt-and-braces against a double entry in the
       metafield ever rendering the same code twice */
    var seen = {};
    list = list.filter(function (c) {
      var k = String(c.code || "").toUpperCase();
      if (!k || seen[k]) return false;
      seen[k] = true;
      return true;
    });

    if (!list.length) {
      body.innerHTML = '<p class="bu-cp-empty">No offers available right now.</p>';
    } else {
      var applied = (d && d.applied) || "";
      /* usable ones first, then the rest */
      var sorted = list.slice().sort(function (a, b) {
        var ae = a.eligible ? 0 : 1;
        var be = b.eligible ? 0 : 1;
        return ae - be;
      });
      body.innerHTML = sorted.map(function (c) { return cardFor(c, applied); }).join("");
    }
    body.scrollTop = 0;
    requestAnimationFrame(function () { sheet.classList.add("bu-open"); });
  }

  function closeSheet() {
    var d = drawer();
    var sheet = d ? d.querySelector(".bu-cp-sheet") : null;
    if (sheet) sheet.classList.remove("bu-open");
  }

  /* ---------------- events ---------------- */
  document.addEventListener("click", function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest("#CartDrawer .bu-coupon-view")) {
      e.preventDefault(); e.stopPropagation();
      openSheet();
      return;
    }
    if (t.closest("#CartDrawer .bu-cp-close") || t.closest("#CartDrawer .bu-cp-handle")) {
      e.preventDefault(); e.stopPropagation();
      closeSheet();
      return;
    }
    var sheet = t.closest("#CartDrawer .bu-cp-sheet");
    if (sheet && !t.closest(".bu-cp-panel")) { closeSheet(); return; }

    /* × inside the coupon field */
    var removeBtn = t.closest("#CartDrawer .bu-coupon-remove");
    if (removeBtn) {
      e.preventDefault(); e.stopPropagation();
      removeBtn.disabled = true;
      remove(function (ok) { if (!ok) removeBtn.disabled = false; });
      return;
    }

    /* Remove from inside the popup */
    var popRemove = t.closest("#CartDrawer .bu-cp-action[data-remove]");
    if (popRemove) {
      e.preventDefault(); e.stopPropagation();
      var wasLabel = popRemove.textContent;
      popRemove.disabled = true;
      popRemove.textContent = "Removing\u2026";
      remove(function (ok) {
        if (!ok) { popRemove.disabled = false; popRemove.textContent = wasLabel; }
      });
      return;
    }

    var applyBtn = t.closest("#CartDrawer .bu-cp-action[data-code]");
    if (applyBtn) {
      e.preventDefault(); e.stopPropagation();
      var original = applyBtn.textContent;
      applyBtn.disabled = true;
      applyBtn.textContent = "Applying\u2026";
      apply(applyBtn.getAttribute("data-code"), function (ok) {
        if (!ok) { applyBtn.disabled = false; applyBtn.textContent = original; }
      });
      return;
    }

    var manual = t.closest("#CartDrawer .bu-coupon-apply");
    if (manual) {
      e.preventDefault(); e.stopPropagation();
      var input = document.querySelector("#CartDrawer .bu-coupon-input");
      if (input && input.value.trim()) apply(input.value);
      return;
    }
  }, true);

  /* show the Apply button only once something has been typed */
  document.addEventListener("input", function (e) {
    var input = e.target && e.target.closest ? e.target.closest("#CartDrawer .bu-coupon-input") : null;
    if (!input) return;
    var btn = input.parentNode.querySelector(".bu-coupon-apply");
    if (btn) btn.hidden = input.value.trim().length === 0;
    message("");
  }, true);

  document.addEventListener("keydown", function (e) {
    var input = e.target && e.target.closest ? e.target.closest("#CartDrawer .bu-coupon-input") : null;
    if (input && e.key === "Enter") {
      e.preventDefault();
      if (input.value.trim()) apply(input.value);
      return;
    }
    if (e.key === "Escape" || e.key === "Esc") {
      var open = document.querySelector("#CartDrawer .bu-cp-sheet.bu-open");
      if (open) { closeSheet(); e.stopPropagation(); }
    }
  }, true);
})();